import React from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import {
    ExternalProviderEnum,
    LoyaltyClub,
    LoyaltyClubSearchParams,
    APP_TARGET_CONFIG,
} from '../../stores/loyalty-club';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useLoyaltyClubStore,
} from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { getItemProp, Lang } from '../../utils';
import { CreateButton } from '../../components/action-buttons';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { PageTopBar } from '../../components/page-top-bar';
import { SearchInput } from '../../components/search-input';
import { Tag, TagVariant } from '../../components/tag';
import style from './style.module.scss';

const formatReadableDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const renderImage = (_: unknown, record: LoyaltyClub) => {
    const image =
        getItemProp(record.translations, Lang[0], 'image') ??
        getItemProp(record.translations, Lang[1], 'image');
    if (!image) return null;
    return (
        <div className={style.image_cell}>
            <img src={image as string} alt="" className={style.thumbnail} />
        </div>
    );
};

const renderIsActive = (_: unknown, record: LoyaltyClub) => {
    return record.active ? (
        <Tag variant="success">Active</Tag>
    ) : (
        <Tag variant="error">Inactive</Tag>
    );
};

const renderAppTarget = (_: unknown, record: LoyaltyClub) => {
    const target = record.appTarget;
    if (!target) {
        return (
            <div className={style.app_target_icons_wrapper}>
                {Object.values(APP_TARGET_CONFIG).map((config) => (
                    <img
                        key={config.icon}
                        src={config.icon}
                        alt={config.label}
                        title={config.label}
                        className={style.app_target_icon}
                    />
                ))}
            </div>
        );
    }
    const info = APP_TARGET_CONFIG[target];
    if (!info) return target;
    return (
        <img
            src={info.icon}
            alt={info.label}
            title={info.label}
            className={style.app_target_icon}
        />
    );
};

const EXTERNAL_PROVIDER_VARIANTS: Record<ExternalProviderEnum, TagVariant> = {
    six12: 'purple',
    lift: 'volcano',
};

const renderExternalProvider = (_: unknown, record: LoyaltyClub) => {
    if (!record.externalProvider) return '—';
    const variant =
        EXTERNAL_PROVIDER_VARIANTS[record.externalProvider] ?? 'grey';
    return <Tag variant={variant}>{record.externalProvider}</Tag>;
};

const renderStartDate = (_: unknown, record: LoyaltyClub) => {
    return record.startDate ? formatReadableDate(record.startDate) : '';
};

const renderEndDate = (_: unknown, record: LoyaltyClub) => {
    return record.endDate ? formatReadableDate(record.endDate) : '';
};

export const LoyaltyClubsPage = observer(() => {
    const loyaltyClubStore = useLoyaltyClubStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();
    const appTargetStore = useApplicationSelectionStore();

    const { appTarget } = appTargetStore;
    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const search = useSearchParams<LoyaltyClubSearchParams>();

    const handleRowClick = React.useCallback(
        (record: LoyaltyClub) => ({
            onClick: () => navigation.goToLoyaltyClubEdit(record.id),
        }),
        [navigation]
    );

    const [inputValue, setInputValue] = React.useState(search.params.q ?? '');
    const debounceRef = React.useRef<number | null>(null);

    React.useEffect(() => {
        return () => {
            if (debounceRef.current !== null) {
                window.clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const handleSearchChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setInputValue(value);
            if (debounceRef.current !== null) {
                window.clearTimeout(debounceRef.current);
            }
            debounceRef.current = window.setTimeout(() => {
                search.setParams({ q: value, page: 0 });
            }, 350);
        },
        [search]
    );

    React.useEffect(() => {
        loyaltyClubStore.fetchPaginatedLoyaltyClubs({
            ...search.params,
            appTarget,
        });
    }, [loyaltyClubStore, search.params, appTarget]);

    const columns: ColumnProps<LoyaltyClub>[] = React.useMemo(
        () => [
            {
                title: 'Active',
                key: 'is_active',
                render: renderIsActive,
                width: 100,
            },
            {
                title: 'Image',
                key: 'image',
                width: 80,
                render: renderImage,
            },
            {
                title: 'Name',
                key: 'name',
                dataIndex: 'name',
                width: 250,
            },
            {
                title: 'App Target',
                key: 'app_target',
                render: renderAppTarget,
                width: 80,
                align: 'center',
            },
            {
                title: 'External Provider',
                key: 'external_provider',
                render: renderExternalProvider,
                width: 130,
            },
            {
                title: 'Start',
                key: 'start_date',
                render: renderStartDate,
                width: 120,
            },
            {
                title: 'End',
                key: 'end_date',
                render: renderEndDate,
                width: 120,
            },
        ],
        []
    );

    return (
        <>
            <PageTopBar title="Loyalty Clubs">
                <CreateButton
                    onClick={() => navigation.goToLoyaltyClubCreate()}
                />
            </PageTopBar>

            <div className="action_zone_above_table">
                <div className="table_header_options_bar">
                    <div className={'query_box'}>
                        <SearchInput
                            searchValue={inputValue}
                            onChange={handleSearchChange}
                            allowClear={true}
                            placeholder="Search"
                        />
                    </div>
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: loyaltyClubStore.paginatedLoyaltyClubs?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            <Table<LoyaltyClub>
                className={`${style.table} clickable_table_body_rows`}
                rowKey="id"
                dataSource={loyaltyClubStore.paginatedLoyaltyClubs?.results}
                columns={columns}
                loading={loyaltyClubStore.isFetchingPaginatedCount > 0}
                pagination={false}
                onRow={handleRowClick}
                size="small"
                bordered
            />
        </>
    );
});
